import React from 'react';
import HistoricalRunsDropdown from './HistoricalRunsDropdown';
import Tooltip from '../AutomationsView/components/Tooltip';
import { formatRelativeTime, formatDate } from './utils';
import cloudApi from '../../../services/cloudApi';

const DatabaseRow = ({
  task,
  isExpanded,
  onToggleExpanded,
  historicalTablesCache,
  setHistoricalTablesCache
}) => {
  const handleViewTable = () => {
    if (task.latestTable?.id) {
      window.open(`/table/${task.latestTable.id}`, '_blank');
    }
  };

  const handleToggleClick = (e) => {
    e.stopPropagation();
    onToggleExpanded(task.id);
  };

  const historyCount = historicalTablesCache[task.id]?.length ?? task.tableCount;
  const rowCount = task.latestTable?.rowCount || 0;
  const relativeTime = formatRelativeTime(task.latestTable?.createdAt);
  const fullDate = task.latestTable?.createdAt ? formatDate(task.latestTable.createdAt, cloudApi) : '';

  return (
    <div
      onClick={handleViewTable}
      className={`relative rounded-2xl border overflow-hidden transition-all duration-200 group/dbrow cursor-pointer
                  ${isExpanded
                    ? 'bg-white dark:bg-white/[0.04] border-gray-200 dark:border-white/[0.08] shadow-md'
                    : 'bg-white dark:bg-white/[0.02] border-gray-200/60 dark:border-white/[0.06] shadow-sm hover:shadow-card-hover dark:hover:shadow-card-hover-dark dark:hover:border-white/[0.1]'
                  }`}
    >
      {/* Left accent gradient line */}
      <div className={`absolute left-0 top-0 bottom-0 transition-all duration-300
                      ${isExpanded
                        ? 'opacity-100 w-1 bg-gradient-to-b from-blue-400 via-cyan-400 to-purple-400 shadow-[2px_0_12px_rgba(34,211,238,0.2)]'
                        : 'opacity-0 w-0.5 group-hover/dbrow:opacity-100 group-hover/dbrow:w-1 bg-gradient-to-b from-blue-400 via-cyan-400 to-purple-400 group-hover/dbrow:shadow-[2px_0_12px_rgba(34,211,238,0.2)]'
                      }`} />

      {/* Card content */}
      <div className="px-3 lg:px-4 py-2.5 lg:py-3">
        {/* Main row */}
        <div className="flex items-center gap-3 lg:gap-4">
          {/* Favicon */}
          <div className="flex-shrink-0">
            {task.metadata?.favicon ? (
              <img
                src={task.metadata.favicon}
                alt=""
                className="w-8 h-8 rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03]"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center text-gray-400 dark:text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>

          {/* Name + URL */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {task.name}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              {task.domain && (
                <span className="text-[11px] text-gray-500 dark:text-gray-500 font-medium tracking-wide">
                  {task.domain}
                </span>
              )}
              {task.metadata?.url && (
                <span className="text-[11px] text-gray-400 dark:text-gray-600 truncate max-w-xs hidden lg:inline" title={task.metadata.url}>
                  {task.metadata.url}
                </span>
              )}
            </div>
          </div>

          {/* Stats - always-visible, consistent structure */}
          <div className="hidden sm:flex items-center gap-4 lg:gap-5 flex-shrink-0">
            {/* Rows */}
            <div className="text-right min-w-[48px]">
              <span className="text-lg font-light text-gray-900 dark:text-gray-100 tabular-nums tracking-tight leading-none">
                {rowCount.toLocaleString()}
              </span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">rows</span>
            </div>

            {/* Updated */}
            <div className="hidden md:block text-right min-w-[56px]" title={fullDate}>
              <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                {relativeTime || '—'}
              </span>
            </div>

            {/* Runs */}
            <div className="hidden lg:block text-right min-w-[40px]">
              <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                {task.tableCount} {task.tableCount === 1 ? 'run' : 'runs'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {/* Open table */}
            <Tooltip content="Open table" borderColor="#6B7280">
              <button
                onClick={(e) => { e.stopPropagation(); handleViewTable(); }}
                className="p-2 rounded-xl text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </button>
            </Tooltip>

            {/* History toggle */}
            <Tooltip content={isExpanded ? 'Hide history' : `History (${historyCount})`} borderColor="#6B7280">
              <button
                onClick={handleToggleClick}
                className={`expand-button p-2 rounded-xl transition-colors
                           ${isExpanded
                             ? 'text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10'
                             : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06]'
                           }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Mobile stats row */}
        <div className="flex items-center gap-4 mt-1.5 pl-11 sm:hidden text-[11px]">
          <div className="flex items-baseline gap-1">
            <span className="text-base font-light text-gray-900 dark:text-gray-100 tabular-nums leading-none">
              {rowCount.toLocaleString()}
            </span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">rows</span>
          </div>
          <span className="text-gray-400 dark:text-gray-500 tabular-nums" title={fullDate}>
            {relativeTime || '—'}
          </span>
          <span className="text-gray-400 dark:text-gray-500 tabular-nums">
            {task.tableCount} {task.tableCount === 1 ? 'run' : 'runs'}
          </span>
        </div>
      </div>

      {/* Historical Runs Dropdown */}
      <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}>
        <div className="min-h-0 overflow-hidden">
          {isExpanded && (
            <HistoricalRunsDropdown
              taskId={task.id}
              currentTableId={task.latestTable?.id}
              historicalTablesCache={historicalTablesCache}
              setHistoricalTablesCache={setHistoricalTablesCache}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseRow;
